import React, { useState, useEffect } from "react";
import usePincodeCheck from "../../../utils/usePincodeCheck";
import useStore from "../../../store/useStore";
import { getActivePincode } from "../../../utils/location";

const PincodeChecker = ({
  initialPincode = "",
  productId = null,
  onPincodeChange,
  className = "",
}) => {
  const defaultPincode = useStore((s) => {
    return getActivePincode(s) || "";
  });
  const [pincode, setPincode] = useState(initialPincode || defaultPincode);
  const { status, message, data, productAvailability } = usePincodeCheck(pincode, productId);

  useEffect(() => {
    if (initialPincode) {
      setPincode(initialPincode);
    }
  }, [initialPincode]);

  const handleInputChange = (e) => {
    const cleanPin = e.target.value.replace(/\D/g, "").slice(0, 6);
    setPincode(cleanPin);
    if (onPincodeChange) {
      onPincodeChange({ pincode: cleanPin, isComplete: cleanPin.length === 6 });
    }
  };

  const isComplete = pincode.length === 6;
  const isServiceable = status === "serviceable";
  const isNotServiceable = status === "not_serviceable" || status === "error";
  const isChecking = status === "checking";

  // Product-level stock info (only present when productId is passed)
  const isOutOfStock =
    isServiceable &&
    productAvailability &&
    productAvailability.product_available === false;

  return (
    <div className={`mt-4 ${className}`}>
      <div className="flex items-center gap-2">
        <div className="text-[11px] font-medium leading-tight text-gray-800 sm:text-xs">
          <div>Check Stock Availability</div>
          <div>Enter Area Pin code</div>
        </div>
        <input
          type="text"
          maxLength={6}
          inputMode="numeric"
          value={pincode}
          onChange={handleInputChange}
          className={`w-24 rounded-lg border-2 bg-white px-2 py-0.5 text-center text-sm font-bold outline-none shadow-sm transition-colors ${
            isChecking
              ? "border-gray-300 text-gray-500"
              : isComplete
                ? isServiceable
                  ? isOutOfStock
                    ? "border-amber-500 text-amber-600 focus:ring-1 focus:ring-amber-500"
                    : "border-[#00a859] text-[#00a859] focus:ring-1 focus:ring-[#00a859]"
                  : isNotServiceable
                    ? "border-[#ea580c] text-[#ea580c] focus:ring-1 focus:ring-[#ea580c]"
                    : "border-gray-300 text-gray-800 focus:ring-1 focus:ring-gray-300"
                : "border-[#00a859] text-[#00a859] focus:ring-1 focus:ring-[#00a859]"
          }`}
          placeholder={defaultPincode || "Pincode"}
        />
      </div>

      {/* Loading State */}
      {isChecking && (
        <div className="mt-1.5 text-[11px] font-bold">
          <span className="flex items-center gap-1.5 text-gray-500">
            <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Checking availability…
          </span>
        </div>
      )}

      {/* Result Messages */}
      {isComplete && !isChecking && (isServiceable || isNotServiceable) && (
        <div className="mt-1.5 text-[11px] font-bold">
          {isNotServiceable ? (
            <span className="flex items-center gap-1 text-[#ea580c]">
              <span>⚠️</span>
              {message || "Delivery is not available for this pincode. Coming soon!"}
            </span>
          ) : isOutOfStock ? (
            <span className="flex flex-col gap-0.5">
              <span className="flex items-center gap-1 text-[#00a859]">
                <span>✓</span>
                Delivery available
                {data?.area_name && (
                  <span className="ml-1 font-normal text-gray-500">({data.area_name})</span>
                )}
              </span>
              <span className="flex items-center gap-1 text-amber-600">
                <span>⚠️</span>
                {productAvailability.message || "This product is currently out of stock at your location"}
              </span>
            </span>
          ) : (
            <span className="flex items-center gap-1 text-[#00a859]">
              <span>✓</span>
              {message || "Delivery is available for this pincode"}
              {data?.area_name && (
                <span className="ml-1 font-normal text-gray-500">({data.area_name})</span>
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default PincodeChecker;
